import { parsePhoneNumberFromString } from 'libphonenumber-js';

/** ISO 3166-1 alpha-2 codes available in panel phone-country filters. */
export const PHONE_COUNTRY_CODES = ['RU', 'UZ', 'KZ', 'KG', 'TJ', 'AZ', 'TM'] as const;

export type PhoneCountryCode = (typeof PHONE_COUNTRY_CODES)[number];

const PHONE_COUNTRY_CODE_SET = new Set<string>(PHONE_COUNTRY_CODES);

export const PHONE_COUNTRY_FILTER_OPTIONS: { code: PhoneCountryCode; labelRu: string }[] = [
	{ code: 'RU', labelRu: 'Россия' },
	{ code: 'UZ', labelRu: 'Узбекистан' },
	{ code: 'KZ', labelRu: 'Казахстан' },
	{ code: 'KG', labelRu: 'Кыргызстан' },
	{ code: 'TJ', labelRu: 'Таджикистан' },
	{ code: 'AZ', labelRu: 'Азербайджан' },
	{ code: 'TM', labelRu: 'Туркменистан' },
];

/** Longest-prefix MSISDN fallback when libphonenumber / country_iso unavailable (SQL parity). */
export const PHONE_COUNTRY_MSISDN_PREFIXES: Record<PhoneCountryCode, readonly string[]> = {
	UZ: ['+998', '998'],
	TJ: ['+992', '992'],
	KG: ['+996', '996'],
	KZ: ['+77', '77', '+76', '76'],
	AZ: ['+994', '994'],
	TM: ['+993', '993'],
	RU: ['+7', '7'],
};

const DEFAULT_PARSE_REGION: PhoneCountryCode = 'UZ';

export function normalizePhoneCountryCode(iso: string | null | undefined): PhoneCountryCode | null {
	const raw = iso?.trim().toUpperCase() ?? '';
	if (!raw) return null;
	if (raw === 'KG') return 'KG';
	if (PHONE_COUNTRY_CODE_SET.has(raw)) return raw as PhoneCountryCode;
	return null;
}

function normalizeMsisdnDigits(msisdn: string): string {
	const trimmed = msisdn.trim();
	if (!trimmed) return '';
	if (trimmed.startsWith('+')) return trimmed;
	return `+${trimmed.replace(/\D/g, '')}`;
}

function resolveCountryFromMsisdnPrefix(msisdn: string): PhoneCountryCode | null {
	const n = msisdn.trim();
	if (!n) return null;
	const candidates: { code: PhoneCountryCode; prefix: string }[] = [];
	for (const code of PHONE_COUNTRY_CODES) {
		for (const prefix of PHONE_COUNTRY_MSISDN_PREFIXES[code]) {
			candidates.push({ code, prefix });
		}
	}
	candidates.sort((a, b) => b.prefix.length - a.prefix.length);
	for (const { code, prefix } of candidates) {
		if (n.startsWith(prefix)) return code;
	}
	return null;
}

export function resolveCountryFromMsisdn(msisdn: string | null | undefined): PhoneCountryCode | null {
	const raw = msisdn?.trim() ?? '';
	if (!raw) return null;
	const normalized = normalizeMsisdnDigits(raw);
	const parsed = parsePhoneNumberFromString(normalized, DEFAULT_PARSE_REGION);
	if (parsed?.country) {
		const fromLib = normalizePhoneCountryCode(parsed.country);
		if (fromLib) return fromLib;
	}
	return resolveCountryFromMsisdnPrefix(raw.startsWith('+') ? raw : normalized);
}

export function resolveEffectivePhoneCountry(
	msisdn: string | null | undefined,
	countryIso: string | null | undefined,
): PhoneCountryCode | null {
	const fromIso = normalizePhoneCountryCode(countryIso);
	if (fromIso) return fromIso;
	return resolveCountryFromMsisdn(msisdn);
}

export function collectClientPhoneCountries(
	clientPhone: string | null | undefined,
	phones: { number?: string | null; countryIso?: string | null }[],
): PhoneCountryCode[] {
	const codes = new Set<PhoneCountryCode>();
	const clientCountry = resolveEffectivePhoneCountry(clientPhone, null);
	if (clientCountry) codes.add(clientCountry);
	for (const phone of phones) {
		const c = resolveEffectivePhoneCountry(phone.number, phone.countryIso);
		if (c) codes.add(c);
	}
	return [...codes].sort();
}

export function clientHasNonEmptyPhoneNumber(
	clientPhone: string | null | undefined,
	phones: { number?: string | null }[],
): boolean {
	if (clientPhone?.trim()) return true;
	return phones.some(p => Boolean(p.number?.trim()));
}

type PhoneSlot = { number: string; countryIso: string | null | undefined };

function collectPhoneCountryExclusionSlots(
	clientPhone: string | null | undefined,
	phones: { number?: string | null; countryIso?: string | null }[],
): PhoneSlot[] {
	const slots: PhoneSlot[] = [];
	if (clientPhone?.trim()) {
		slots.push({ number: clientPhone.trim(), countryIso: null });
	}
	for (const phone of phones) {
		const number = phone.number?.trim() ?? '';
		const hasNumber = Boolean(number);
		const hasIso = normalizePhoneCountryCode(phone.countryIso) !== null;
		if (hasNumber || hasIso) {
			slots.push({ number, countryIso: phone.countryIso });
		}
	}
	return slots;
}

/** True when any exclusion slot has no resolved country (SQL: treated as outside excluded set). */
export function clientHasUnclassifiedNonEmptyPhone(
	clientPhone: string | null | undefined,
	phones: { number?: string | null; countryIso?: string | null }[],
): boolean {
	return collectPhoneCountryExclusionSlots(clientPhone, phones).some(
		slot => resolveEffectivePhoneCountry(slot.number, slot.countryIso) === null,
	);
}

/**
 * Client-level exclusion with full phone list (parity with SQL `buildClientPassesPhoneCountryExclusionWhere`).
 * Show if any slot is unclassified or not in `excluded`; hide only when every slot is in `excluded`.
 */
export function clientPassesPhoneCountryExclusionForClient(
	excluded: readonly PhoneCountryCode[],
	clientPhone: string | null | undefined,
	phones: { number?: string | null; countryIso?: string | null }[],
): boolean {
	if (!excluded.length) return true;
	const slots = collectPhoneCountryExclusionSlots(clientPhone, phones);
	if (!slots.length) return true;
	const excludedSet = new Set(excluded);
	return slots.some(slot => {
		const country = resolveEffectivePhoneCountry(slot.number, slot.countryIso);
		return country === null || !excludedSet.has(country);
	});
}

/**
 * Client-level feed filter from aggregate `phoneCountryCodes` (realtime payload).
 * `hasExclusionSubject`: non-empty MSISDN and/or known SIM countries already in `countries`.
 * Incomplete without `hasUnclassifiedNonEmptyPhone` when SIM rows are omitted from codes.
 * No exclusion slots (no numbers and no allowlisted country_iso) → passes.
 */
export function clientPassesPhoneCountryExclusion(
	excluded: readonly PhoneCountryCode[],
	countries: readonly PhoneCountryCode[],
	hasExclusionSubject: boolean,
	hasUnclassifiedNonEmptyPhone?: boolean,
): boolean {
	if (!excluded.length) return true;
	const subject = hasExclusionSubject || countries.length > 0;
	if (!subject) return true;
	if (hasUnclassifiedNonEmptyPhone) return true;
	if (!countries.length) return true;
	const excludedSet = new Set(excluded);
	return countries.some(c => !excludedSet.has(c));
}

/** Phones tab: hide row when effective country is in excluded set. */
export function phoneRowPassesPhoneCountryExclusion(
	excluded: readonly PhoneCountryCode[],
	country: PhoneCountryCode | null,
): boolean {
	if (!excluded.length) return true;
	if (!country) return true;
	return !excluded.includes(country);
}

export function formatExcludePhoneCountriesChipLabel(excluded: readonly PhoneCountryCode[]): string {
	if (!excluded.length) return '';
	const sorted = [...excluded].sort();
	return `Искл.: ${sorted.join(', ')}`;
}
